import React, { useEffect, useState } from 'react';
import { Query, graphql, compose, Subscription } from 'react-apollo';
import styled from 'styled-components';
import Page, { Grid, GridColumn } from '@atlaskit/page';
import Tick from '@atlaskit/icon/glyph/check-circle';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import Flag, { FlagGroup } from '@atlaskit/flag';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { token } from '@atlaskit/tokens';
import ComponentLabelsSelect, { GREEN2, RED2 } from '../components/component-labels';
import { SYNC_ERRORS, SYNC_DATA, JIRA_PROJECTS_QUERY } from '../graphql/queries';
import { APP_INSTALL, CREATE_JIRA_PROJECT_ID } from '../graphql/mutations';
import Loading from '../components/virtual-components/Loading';
import { COMPONENTS_IMPORT_PROGRESS } from '../graphql/subscriptions';
import { ErrorParser } from '../utils/ErrorParser';
import { SYNC_ERRORS_MESSAGES } from '../utils/consts';
import useJiraContext from '../hooks/useJiraContext';

const Message = styled.p`
    color: ${token('color.text')};
`;

const Wrapper = styled.div`
    width: 100%;
    height: 100%;
    margin-top: 50px;
`;

const SelectWrapper = styled.div`
    margin-top: 50px;
    width: 50%;
`;

const StyledFlagGroup = styled(FlagGroup)`
    bottom: 200px !important;
`;

const ProcessWrapper = (props) => {
    const { project, synchronize, handleSetFlag, dismissFlag } = props;
    return (
        <Subscription subscription={COMPONENTS_IMPORT_PROGRESS}>
            {({ data, loading }) => {
                let progressValue = 0;
                if (!loading) progressValue = (data?.componentsImportProgress.percent || 0) / 100;
                return (
                    <ComposedComponentLabelsSelect
                        project={project}
                        synchronize={synchronize}
                        handleSetFlag={handleSetFlag}
                        dismissFlag={dismissFlag}
                        progressValue={progressValue}
                    />
                );
            }}
        </Subscription>
    );
};

const ComposedComponentLabelsSelect = compose(graphql(CREATE_JIRA_PROJECT_ID, { name: 'createJiraProjectId' }))(
    ComponentLabelsSelect,
);

const ComponentLabels = (props) => {
    const [, setHasSyncErrors] = useState(null);
    const [, setFetchedSyncErrors] = useState(false);
    const [flags, setFlags] = useState([]);
    // SYNC DATA
    const [, setSyncLoading] = useState(false);
    const { projectKey } = useJiraContext();

    useEffect(() => {
        window.PlatformAdapter.resizeWindow('100%', '800px');
    }, []);
    const handleSetFlag = (flag) => {
        setFlags([flag]);
    };

    const dismissFlag = () => {
        setFlags(flags.slice(1));
    };

    const _handleSync = async (onDone) => {
        setSyncLoading(true);
        return props
            .synchronize({
                refetchQueries: [{ query: SYNC_ERRORS }, { query: SYNC_DATA }],
                awaitRefetchQueries: true,
            })
            .then((res) => {
                setSyncLoading(false);
                // if no sync errors occurred
                if (res.data.synchronize.success) {
                    setFlags([
                        {
                            icon: <Tick primaryColor={GREEN2} />,
                            title: 'Synchronization successful. Importing...',
                            key: 'success',
                            autoDismiss: true,
                        },
                    ]);

                    if (typeof onDone === 'function') {
                        onDone(false);
                    }
                    return { success: true };
                }
                // if any sync error occurred

                setFetchedSyncErrors(true);
                setHasSyncErrors(true);
                setFlags([
                    {
                        icon: <ErrorIcon primaryColor={RED2} />,
                        title: 'One or more conflicts occurred.',
                        key: 'conflict',
                        autoDismiss: true,
                    },
                ]);
                return { success: false };
            })
            .catch((err) => {
                setSyncLoading(false);
                if (err?.message && SYNC_ERRORS_MESSAGES[ErrorParser.parse(err.message)]) {
                    setFlags([
                        {
                            icon: <WarningIcon primaryColor={RED2} />,
                            title: SYNC_ERRORS_MESSAGES[ErrorParser.parse(err.message)].title,
                            description: SYNC_ERRORS_MESSAGES[ErrorParser.parse(err.message)].message,
                            key: 'Warning',
                            autoDismiss: false,
                            actions: [
                                {
                                    content: 'Dismiss',
                                    onClick: () => {
                                        setFlags([]);
                                    },
                                },
                            ],
                        },
                    ]);
                } else {
                    setFlags([
                        {
                            icon: <ErrorIcon primaryColor={RED2} />,
                            title: ErrorParser.parse(err.message) || 'One or more errors occurred.',
                            key: 'error',
                        },
                    ]);
                }

                console.log('sync error: ', err);
                return { success: false };
            });
    };
    return (
        <Query query={JIRA_PROJECTS_QUERY}>
            {({
                loading: loadingProjects,
                error: projectsError,
                data: projectsData,
                networkStatus: projectsNetworkStatus,
            }) => {
                if (loadingProjects || projectsNetworkStatus === 4) return <Loading />;
                if (projectsError) return `Error! ${projectsError.message}`;

                const tmp = projectsData.jiraProjects.filter((project) => project.key === projectKey);
                return (
                    <Page>
                        <Grid>
                            <GridColumn>
                                <Wrapper>
                                    <h1>Import components</h1>
                                    <Message>
                                        You can import all components that are in your Jira or choose the component
                                        label to import all tagged components.{' '}
                                        <a
                                            href="https://apps.appf.re/oc/doc/labels"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Learn more about component labels.
                                        </a>
                                    </Message>

                                    <SelectWrapper>
                                        <ProcessWrapper
                                            project={tmp}
                                            synchronize={_handleSync}
                                            handleSetFlag={handleSetFlag}
                                            dismissFlag={dismissFlag}
                                        />
                                    </SelectWrapper>
                                    <StyledFlagGroup onDismissed={dismissFlag}>
                                        {flags.map((flag) => (
                                            <Flag key={flag.key} {...flag} />
                                        ))}
                                    </StyledFlagGroup>
                                </Wrapper>
                            </GridColumn>
                        </Grid>
                    </Page>
                );
            }}
        </Query>
    );
};

export default compose(graphql(APP_INSTALL, { name: 'synchronize' }))(ComponentLabels);
